import { createSlice, createSelector } from '@reduxjs/toolkit';

const websocketSlice = createSlice({
  name: 'websocket',
  initialState: {
    connected: false,
    reconnecting: false,
    error: null,
    quality: 'disconnected', // good, fair, poor, connecting, disconnected
    lastHeartbeat: null,
    responseTime: null,
    missedHeartbeats: 0,
    reconnectAttempts: 0,
    maxAttemptsReached: false,
    reason: null,
    strategy: 'normal',
    nextAttemptIn: null,
    health: {
      connected: false,
      quality: 'disconnected',
      lastHeartbeat: null,
      missedHeartbeats: 0,
      reconnectAttempts: 0,
      isPageVisible: true,
      strategy: 'normal'
    },
    showConnectionAlert: false,
    alertType: 'info', // info, warning, error, success
    alertMessage: null,
    timestamp: null
  },
  reducers: {
    updateWebSocketStatus: (state, action) => {
      const newState = { ...state, ...action.payload };
      
      // Auto-determine if we should show connection alerts
      if (action.payload.connected === false && state.connected === true) {
        // Connection lost
        newState.showConnectionAlert = true;
        newState.alertType = 'error';
        newState.alertMessage = `Connection lost${action.payload.reason ? `: ${action.payload.reason}` : ''}`;
      } else if (action.payload.connected === true && state.connected === false) {
        // Connection restored
        newState.showConnectionAlert = true;
        newState.alertType = 'success';
        newState.alertMessage = 'Connection restored';
      } else if (action.payload.maxAttemptsReached) {
        // Max attempts reached
        newState.showConnectionAlert = true;
        newState.alertType = 'error';
        newState.alertMessage = 'Connection failed. Please refresh the page.';
      } else if (action.payload.quality === 'poor' && state.quality !== 'poor') {
        // Connection quality degraded
        newState.showConnectionAlert = true;
        newState.alertType = 'warning';
        newState.alertMessage = 'Connection quality is poor';
      }
      
      return newState;
    },
    dismissConnectionAlert: (state) => {
      state.showConnectionAlert = false;
      state.alertMessage = null;
    },
    showCustomAlert: (state, action) => {
      state.showConnectionAlert = true;
      state.alertType = action.payload.type || 'info';
      state.alertMessage = action.payload.message;
    }
  },
});

export const { 
  updateWebSocketStatus, 
  dismissConnectionAlert, 
  showCustomAlert 
} = websocketSlice.actions;

// Memoized Selectors
export const selectConnectionStatus = createSelector(
  (state) => state.websocket.connected,
  (state) => state.websocket.quality,
  (state) => state.websocket.reconnecting,
  (state) => state.websocket.error,
  (state) => state.websocket.tokenStatus,
  (connected, quality, reconnecting, error, tokenStatus) => ({
    connected,
    quality,
    reconnecting,
    error,
    tokenStatus
  })
);

export const selectConnectionHealth = (state) => state.websocket.health;

export const selectConnectionAlert = createSelector(
  (state) => state.websocket.showConnectionAlert,
  (state) => state.websocket.alertType,
  (state) => state.websocket.alertMessage,
  (show, type, message) => ({
    show,
    type,
    message
  })
);

export default websocketSlice.reducer;
